<template>
    <div v-if="loading" class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-2">
        <div v-for="n in 6" :key="n" :class="[n == 1 && 'tw-from-primary-600 tw-to-primary-300 tw-bg-gradient-to-br']" class="tw-bg-white tw-p-4 tw-h-[81px] tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <div :class="[n == 1 && 'tw-bg-primary-300']" class="tw-bg-gray-200 tw-animate-pulse tw-h-5 tw-w-1/3 tw-mb-2 tw-rounded-sm"></div>
            <div :class="[n == 1 && 'tw-bg-primary-300']" class="tw-bg-gray-200 tw-animate-pulse tw-h-6 tw-w-1/2 tw-rounded-sm"></div>
        </div>
    </div>
    <div v-else class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-2">
        <!-- Original content remains the same -->
        <div class="tw-from-primary-600 tw-to-primary-300 tw-bg-gradient-to-br tw-text-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-emerald-200">
            <p class="tw-text-white">{{ $t('common.orders') }}</p>
            <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(totalOrders, {}) }}</div>
        </div>
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.total_sales') }}</p>
            <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(totalSales, {}) }}</div>
        </div>
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.avg_order_value') }}</p>
            <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(avgOrderValue, {}) }}</div>
        </div>
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.confirmed') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(confirmed, {}) }}</div>
                <div class="tw-text-sm tw-font-semibold tw-text-emerald-500">{{ confirmedPercentage }}%</div>
            </div>
        </div>
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.delivered') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(totalDelivered, {}) }}</div>
                <div class="tw-text-sm tw-font-semibold tw-text-emerald-500">{{ deliveredPercentage }}%</div>
            </div>
        </div>
        <div class="tw-bg-white tw-rounded tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200">
            <p class="tw-text-gray-500">{{ $t('common.ads') }}</p>
            <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-text-2xl tw-font-semibold">{{ formatNumber(ad_spend) }}</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { status } from '@/api/products'
import { ref, computed, inject } from 'vue'

const id = inject('id');
const loading = ref(true);
const data = ref([]);
const ad_spend = ref(0);

const totalOrders = computed(() => data.value.reduce((acc, item) => acc + item.total_orders, 0))
const totalOrdersWithoutDuplicated = computed(() => data.value.reduce((acc, item) => acc + (item.agent_status != 'duplicate' ? item.total_orders : 0), 0))
const totalSales = computed(() => data.value.reduce((acc, item) => acc + item.total, 0))
const avgOrderValue = computed(() => totalOrders.value > 0 ? totalSales.value / totalOrders.value : 0)

const confirmed = computed(() => data.value.filter(item => item.agent_status === 'confirmed').reduce((acc, item) => acc + item.total_orders, 0))
const delivered = computed(() => data.value.filter(item => item.delivery_status === 'delivered').reduce((acc, item) => acc + item.total_orders, 0))
const settled = computed(() => data.value.filter(item => item.delivery_status === 'settled').reduce((acc, item) => acc + item.total_orders, 0))

const totalDelivered = computed(() => delivered.value + settled.value)

const confirmedPercentage = computed(() => totalOrdersWithoutDuplicated.value > 0 ? (confirmed.value / totalOrdersWithoutDuplicated.value * 100).toFixed(2) : '0.00')
const deliveredPercentage = computed(() => confirmed.value > 0 ? (totalDelivered.value / confirmed.value * 100).toFixed(2) : '0.00')

const formatNumber = (number, type = {style: 'currency', currency: 'PKR'}) => {
    const num = typeof number === 'number' ? number : 0
    return new Intl.NumberFormat('en', { 
        maximumFractionDigits: 2,
        ...(number === 0 && type.style === 'currency' ? { style: 'decimal' } : type)
    }).format(num)
}

const getData = async () => {
    loading.value = true
    try {
        const response = await status(id)
        data.value = response.data.data
        ad_spend.value = response.data.ad_spend
        
    } catch (error) {
        console.error(error)
        data.value = []
    } finally {
        loading.value = false
    }
}

getData()
</script>